import { z } from "zod";
import { SystemEntity } from "@erp/shared-types";
import { defineStepConfig } from "../../registry/helpers";
import { entityOfType } from "../../utils/system-entities";

const itemMatchStrategySchema = z.object({
  itemField: z
    .enum(["supplierItem", "item", "keyword1"])
    .describe(
      "Which item-master field to compare: SUPPLIER ITEM, the AS400 ITEM number, or KEYWORD-1.",
    ),
  quoteField: z
    .enum(["product", "description"])
    .describe("Which quote field to compare it with."),
  prefix: z
    .string()
    .default("")
    .describe("Prefix added to the quote value before comparing, e.g. BUR."),
  clean: z
    .boolean()
    .default(false)
    .describe("Strip spaces and dashes from the quote value first."),
});

export type SpaRenewalItemMatchStrategy = z.infer<
  typeof itemMatchStrategySchema
>;

/**
 * The twelve matching rungs the Hubbell Alteryx flow tried, in its order:
 * the supplier's item number, then `BUR` + the quoted product or description
 * (raw and with spaces/dashes removed), then the same against the supplier
 * item and keyword fields. Each rung only sees what the rungs above left
 * unmatched.
 */
export const SPA_RENEWAL_DEFAULT_ITEM_MATCH_STRATEGIES: readonly SpaRenewalItemMatchStrategy[] =
  [
    {
      itemField: "supplierItem",
      quoteField: "product",
      prefix: "",
      clean: false,
    },
    { itemField: "item", quoteField: "product", prefix: "BUR", clean: false },
    {
      itemField: "item",
      quoteField: "description",
      prefix: "BUR",
      clean: false,
    },
    { itemField: "item", quoteField: "product", prefix: "BUR", clean: true },
    {
      itemField: "item",
      quoteField: "description",
      prefix: "BUR",
      clean: true,
    },
    {
      itemField: "supplierItem",
      quoteField: "description",
      prefix: "",
      clean: false,
    },
    {
      itemField: "supplierItem",
      quoteField: "product",
      prefix: "",
      clean: true,
    },
    {
      itemField: "supplierItem",
      quoteField: "description",
      prefix: "",
      clean: true,
    },
    { itemField: "keyword1", quoteField: "product", prefix: "", clean: false },
    {
      itemField: "keyword1",
      quoteField: "description",
      prefix: "",
      clean: false,
    },
    { itemField: "keyword1", quoteField: "product", prefix: "", clean: true },
    {
      itemField: "keyword1",
      quoteField: "description",
      prefix: "",
      clean: true,
    },
  ];

/**
 * Step 3 of the SPA mass renewal: everything after the inputs are read.
 * Selects the loads that renew, resolves each quoted product to an AS400
 * item, works out whose load it is and how they sell, and writes the FINAL
 * load sheet plus the exception workbooks.
 *
 * Replaces the remaining ~300 Alteryx nodes: the cross-reference joins, the
 * duplicate and negative-margin checks, the twelve item-matching joins, the
 * "feedback" account/sales/segment resolution and the final assembly.
 */
export const spaRenewalBuildLoadSheetStep = defineStepConfig({
  type: "spa_renewal_build_load_sheet",
  name: "SPA Renewal: Build Load Sheet",
  description:
    "Matches the quotes to the AS400 loads they renew, resolves quoted products to AS400 items through a " +
    "configurable cascade of matching strategies, picks the account, sales and market segment for each load, " +
    "and writes FINAL.xlsx (one sheet per division) plus the exception workbooks (duplicates, negative margins, " +
    "loads with no cross-reference or quote, loads to mass-expire, …).",
  category: "Transformation",
  libraryModule: "Pricing",

  inputSchema: z.object({
    quoteLinesDocument: entityOfType(SystemEntity.CORE_DOCUMENT).describe(
      "From SPA Renewal: Read Quotes.",
    ),
    crossReferenceDocument: entityOfType(SystemEntity.CORE_DOCUMENT)
      .optional()
      .describe(
        "From SPA Renewal: Read Quotes. Not needed when the SPA keeps its number.",
      ),
    loadsDocument: entityOfType(SystemEntity.CORE_DOCUMENT).describe(
      "From SPA Renewal: Read AS400 Loads.",
    ),
    itemsDocument: entityOfType(SystemEntity.CORE_DOCUMENT).describe(
      "From SPA Renewal: Read AS400 Loads.",
    ),
    referenceDocuments: z
      .array(entityOfType(SystemEntity.CORE_DOCUMENT))
      .optional()
      .describe(
        "Reference workbooks in any order: AS400 contract groups (GROUPS ACTIVE ACC), customer master " +
          "(CLASSES ACTIVE ACC), MARKET SEGMENTS, and the BI twelve-month sales adhoc report. Recognized by their " +
          "headers. A table none of them carries is read from the Data Asset chosen for it in the step config.",
      ),
  }),

  outputSchema: z.object({
    finalDocument: entityOfType(SystemEntity.CORE_DOCUMENT).describe(
      "FINAL.xlsx — the renewal load sheet.",
    ),
    exceptionDocuments: z
      .array(entityOfType(SystemEntity.CORE_DOCUMENT))
      .describe(
        "One workbook per exception report, named as the Alteryx flow named them.",
      ),
    exceptionDocumentIds: z
      .array(z.string())
      .describe("The same, as ids for send_email attachments."),
    finalDocumentId: z.string(),
    finalRowCount: z.number().int(),
    renewalLoadCount: z.number().int(),
    unmatchedItemCount: z.number().int(),
    stats: z.record(z.string(), z.number()),
  }),

  configSchema: z.object({
    groupsDataAssetId: z
      .string()
      .optional()
      .describe(
        "Data Asset holding GROUPS ACTIVE ACC, read when no uploaded workbook carries the contract groups.",
      ),
    classesDataAssetId: z
      .string()
      .optional()
      .describe(
        "Data Asset holding CLASSES ACTIVE ACC, read when no uploaded workbook carries the customer master.",
      ),
    marketSegmentsDataAssetId: z
      .string()
      .optional()
      .describe(
        "Data Asset holding MARKET SEGMENTS, read when no uploaded workbook carries the market segments.",
      ),
    salesDataAssetId: z
      .string()
      .optional()
      .describe(
        "Data Asset holding the BI twelve-month sales adhoc report, read when no uploaded workbook carries sales.",
      ),
    itemMatchStrategies: z
      .array(itemMatchStrategySchema)
      .min(1)
      .default(() =>
        SPA_RENEWAL_DEFAULT_ITEM_MATCH_STRATEGIES.map((strategy) => ({
          ...strategy,
        })),
      )
      .describe(
        "Tried in order; a quote line stops at the first strategy that finds an item.",
      ),
    keepLowestCostAcrossQuotes: z
      .boolean()
      .default(true)
      .describe(
        "When two quotes price the same item on the same load, keep the cheaper.",
      ),
    vendorItemPrefixes: z
      .array(z.string().min(1))
      .min(1)
      .default(["HUB", "BUR", "KIL"])
      .describe("Must match the Read AS400 Loads step."),
    spaNumberLength: z.number().int().positive().default(8),
    asOfDate: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/)
      .optional()
      .describe(
        "The date loads and quotes are judged current against. Defaults to today.",
      ),
    costAdderIncreaseStep: z
      .number()
      .default(0.01)
      .describe("Recommended cost adder = prior + step, capped."),
    costAdderCap: z.number().default(0.03),
    titleWidth: z
      .number()
      .int()
      .positive()
      .default(20)
      .describe("AS400 agreement title width."),
    titleDedupeWidth: z
      .number()
      .int()
      .positive()
      .default(18)
      .describe(
        "Loads whose titles share this many leading characters get numbered.",
      ),
    accountWidth: z
      .number()
      .int()
      .positive()
      .default(5)
      .describe("Account numbers are right-aligned to this width."),
    splitSheetsByDivision: z.boolean().default(true),
    asciiOnlyOutput: z
      .boolean()
      .default(true)
      .describe(
        "Fold accents and typographic punctuation to ASCII and drop any other non-ASCII character in FINAL; the AS400 load cannot take them.",
      ),
    includeDiagnosticColumns: z
      .boolean()
      .default(false)
      .describe(
        "Add the matching strategy, prior margins, AM and customer name columns to FINAL.",
      ),
    outputColumns: z
      .array(z.string())
      .optional()
      .describe(
        "Override the FINAL column list and order. Unknown names are emitted empty.",
      ),
  }),

  execution: {
    startToCloseTimeout: "30 minutes",
  },

  emits: [] as const,
});
